import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import {
  Alert,
  AlertDescription,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@roger/ui-components';
import { AlertTriangle } from 'lucide-react';
import ProductUpdatesManagement from '../../components/internal-tools/ProductUpdatesManagement';
import { OnboardingTeamPanel } from '../../components/internal-tools/OnboardingTeamPanel';
import { DemoEnvironmentsPanel } from '../../components/internal-tools/DemoEnvironmentsPanel';
import AgencyDataFillPanel from '../../components/internal-tools/AgencyDataFillPanel';
import { isDemoAgencySetupPanelEnabled } from '../../components/internal-tools/demoAgencySetupUi';
import EvvCaptureCorrectionPanel from '../../components/internal-tools/EvvCaptureCorrectionPanel';
import AgencyTimezonePanel from '../../components/internal-tools/AgencyTimezonePanel';
import TemporalScalingOverridesPanel from '../../components/internal-tools/TemporalScalingOverridesPanel';
import SuperUserTab from '../../components/internal-tools/SuperUserTab';
import { StediWebhookReplayPanel } from '../../components/internal-tools/StediWebhookReplayPanel';
import { UnassignedFaxesPanel } from '../../components/internal-tools/UnassignedFaxesPanel';
import { FaxNumbersPanel } from '../../components/internal-tools/FaxNumbersPanel';
import { PayorsPanel } from '../../components/internal-tools/PayorsPanel';
import { ClinicianDirectoryPanel } from '../../components/internal-tools/ClinicianDirectoryPanel';
import { FaxTypesPanel } from '../../components/internal-tools/FaxTypesPanel';
import { TelnyxFaxLinesPanel } from '../../components/internal-tools/TelnyxFaxLinesPanel';
import { SimulatedOrderFaxesPanel } from '../../components/internal-tools/SimulatedOrderFaxesPanel';
import {
  groupInternalToolsSections,
  visibleInternalToolsSections,
} from '../../constants/internalToolsSections';
import { PermissionSlug } from '../../constants/featureFlags';

const InternalTools: React.FC = () => {
  const { hasPermission, user } = useStore(
    useShallow(state => ({
      hasPermission: state.hasPermission,
      user: state.user,
    }))
  );
  const [searchParams, setSearchParams] = useSearchParams();
  // Bumped when linking a number also collects parked faxes. The two fax
  // panels are force-mounted siblings, so the queue does not remount and has
  // to be told that its rows just moved.
  const [claimedFaxes, setClaimedFaxes] = useState(0);

  // The system name is the agency's identity; the written name is only its
  // print / letterhead name and may be edited to anything.
  const isStrawhat = user?.agency?.system_name === 'Strawhat';
  const isSuperUser = hasPermission(PermissionSlug.ADMIN_SUPER_USER);

  // False in production, where the demo-agency-setup API is unmounted too.
  const isDemoSectionEnabled = isDemoAgencySetupPanelEnabled();
  // The caller's own flags, so a section whose API is flag-gated is absent
  // rather than present and answering 403.
  const visibleSections = visibleInternalToolsSections(
    isDemoSectionEnabled,
    user?.feature_flags ?? []
  );

  const isDataFillSectionVisible = visibleSections.some(
    section => section.id === 'agency-data-fill'
  );

  const requestedSection = searchParams.get('section');
  const activeSection =
    visibleSections.find(section => section.id === requestedSection)?.id ??
    visibleSections[0]?.id ??
    '';

  // The strip had grown past the width of the page, and two of its entries
  // were the same subject. Grouping is presentation only: `?section=` still
  // names the section, so an existing link still lands on it and simply opens
  // its parent tab too.
  const groups = groupInternalToolsSections(visibleSections);
  const activeGroup =
    groups.find(group => group.sections.some(s => s.id === activeSection))
      ?.id ??
    groups[0]?.id ??
    '';

  // Every panel stays mounted, the way all six did when they were stacked on one
  // page: an operator who clicks another tab mid-run should not lose a filled-in
  // demo setup form, a Stedi replay selection, or an open product-update editor.
  // Radix unmounts inactive content by default, and its `forceMount` only forces
  // presence -- it leaves `hidden` unset -- so the inactive panel is hidden here
  // off its own `data-state` instead.
  const tabContentClassName = 'mt-4 space-y-6 data-[state=inactive]:hidden';

  const handleSectionChange = (sectionId: string) => {
    // `replace` so tabbing around does not fill the back button with each stop.
    setSearchParams(
      previous => {
        const next = new URLSearchParams(previous);
        next.set('section', sectionId);
        return next;
      },
      { replace: true }
    );
  };

  if (!isStrawhat || !isSuperUser) {
    return (
      <div>
        <Alert className='border-red-200 bg-red-50'>
          <AlertDescription className='text-red-800 flex gap-2 items-center'>
            <AlertTriangle className='h-4 w-4 text-red-600' />
            You do not have permission to view this page. Please contact your
            administrator if you need access.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <Tabs
        value={activeSection}
        onValueChange={handleSectionChange}
        className='w-full'
      >
        {/* Underline tabs, copied from the Admin panel: this page sits at the
            same level in the app, and two different tab treatments read as two
            conventions. */}
        <TabsList className='h-auto w-full justify-start gap-1.5 overflow-x-auto rounded-none border-b border-border/60 bg-transparent p-0'>
          {groups.map(group => (
            <TabsTrigger
              key={group.id}
              // Selecting a parent lands on its first sub-section, which is
              // what the strip's own value has to be for Radix to mark it
              // active. A parent tab is a place, not a panel.
              value={group.sections[0].id}
              data-state={group.id === activeGroup ? 'active' : 'inactive'}
              className='shrink-0 whitespace-nowrap rounded-none border-b-2 border-transparent bg-transparent px-3 py-2 text-sm font-medium text-muted-foreground shadow-none transition-all hover:border-border hover:bg-muted/30 hover:text-foreground data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none'
            >
              {group.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* The sub-strip, shown only for a parent that holds more than one
            section. Rendered outside TabsContent so it sits above whichever
            panel is showing rather than inside one of them. */}
        {groups
          .filter(
            group => group.id === activeGroup && group.sections.length > 1
          )
          .map(group => (
            <div
              key={group.id}
              className='mt-3 flex flex-wrap gap-1.5 border-b border-border/40 pb-1'
            >
              {group.sections.map(section => (
                <button
                  key={section.id}
                  type='button'
                  aria-current={
                    section.id === activeSection ? 'page' : undefined
                  }
                  onClick={() => handleSectionChange(section.id)}
                  className={`shrink-0 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                    section.id === activeSection
                      ? 'bg-muted text-foreground'
                      : 'text-muted-foreground hover:bg-muted/40 hover:text-foreground'
                  }`}
                >
                  {section.label}
                </button>
              ))}
            </div>
          ))}

        {/* Rendered conditionally rather than left to the inactive-tab check:
            the panel must be absent in production, not merely unreachable. */}
        {isDemoSectionEnabled && (
          <TabsContent
            value='demo-environments'
            forceMount
            className={tabContentClassName}
          >
            <DemoEnvironmentsPanel />
          </TabsContent>
        )}

        {/* Rendered only when the section is visible, like the demo panel
            above. forceMount mounts the child whatever tab is active, so an
            unconditional block would fetch the agency list -- and be refused --
            on every Internal Tools load for a super user without the flag. */}
        {isDataFillSectionVisible && (
          <TabsContent
            value='agency-data-fill'
            forceMount
            className={tabContentClassName}
          >
            <AgencyDataFillPanel />
          </TabsContent>
        )}

        <TabsContent
          value='agencies-users'
          forceMount
          className={tabContentClassName}
        >
          <SuperUserTab />
          <AgencyTimezonePanel />
          <OnboardingTeamPanel />
        </TabsContent>

        {/* Not forceMount, unlike its siblings: mounted only while its tab is
            open, so the roster is not fetched on every Internal Tools load.
            Switching tabs drops its filters and page on purpose. */}
        <TabsContent
          value='clinician-directory'
          className={tabContentClassName}
        >
          <ClinicianDirectoryPanel />
        </TabsContent>

        <TabsContent
          value='product-updates'
          forceMount
          className={tabContentClassName}
        >
          <ProductUpdatesManagement />
        </TabsContent>

        <TabsContent value='stedi' forceMount className={tabContentClassName}>
          <StediWebhookReplayPanel />
        </TabsContent>

        <TabsContent value='evv' forceMount className={tabContentClassName}>
          <EvvCaptureCorrectionPanel />
        </TabsContent>

        <TabsContent
          value='unassigned-faxes'
          forceMount
          className={tabContentClassName}
        >
          <UnassignedFaxesPanel reloadToken={claimedFaxes} />
        </TabsContent>

        <TabsContent
          value='fax-numbers'
          forceMount
          className={tabContentClassName}
        >
          <div className='space-y-6'>
            <FaxNumbersPanel
              onParkedFaxesChanged={() => setClaimedFaxes(n => n + 1)}
            />
            <TelnyxFaxLinesPanel />
            {/* Beneath the numbers, because routing a fax to an agency comes
                before saying what the fax is, and both are staff work on the
                same screen. */}
            <FaxTypesPanel />
          </div>
        </TabsContent>

        <TabsContent
          value='simulated-order-faxes'
          forceMount
          className={tabContentClassName}
        >
          <SimulatedOrderFaxesPanel />
        </TabsContent>
        <TabsContent value='payors' forceMount className={tabContentClassName}>
          <PayorsPanel />
        </TabsContent>
        <TabsContent
          value='temporal-scaling'
          forceMount
          className={tabContentClassName}
        >
          <TemporalScalingOverridesPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default InternalTools;
